import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge, aed, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import jsPDF from "jspdf";

export const CHARGE_LABELS = { transportation: "Transportation", stationery: "Stationery", import: "Import charges", airline: "Airline", dispatch: "Dispatch" };
export const chargeLabel = (r) => {
  const base = CHARGE_LABELS[r.charge_type] || r.charge_type;
  return r.mode ? `${base} (${r.mode === "air_cargo" ? "air cargo" : "courier"})` : base;
};

export const downloadDispatchInvoicePdf = async (r) => {
  const doc = new jsPDF();
  const blue = [13, 35, 64], gold = [242, 183, 5];
  doc.setFillColor(...blue); doc.rect(0, 0, 210, 34, "F");
  try {
    const dataUrl = await fetch("/heliopolis-logo.png").then((b) => b.blob()).then((b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); }));
    doc.addImage(dataUrl, "PNG", 14, 8, 18, 18);
  } catch (e) { /* logo optional */ }
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.text("HELIOPOLIS PHARMACEUTICALS", 37, 16);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text("Dispatch Invoice · AED (Dirhams)", 37, 23);
  doc.setFillColor(...gold); doc.rect(0, 34, 210, 1.5, "F");
  let y = 50;
  doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text(r.dispatch_invoice_number, 14, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110, 110, 110);
  doc.text(`Status: ${String(r.status).toUpperCase()}`, 196, y, { align: "right" });
  y += 12;
  [["Dispatch #", r.dispatch_number || "—"], ["Order #", r.order_number || "—"], ["Shipment #", r.shipment_number || "—"], ["Type", chargeLabel(r)]].forEach(([k, v]) => { doc.setTextColor(120, 120, 120); doc.text(k, 14, y); doc.setTextColor(20, 20, 20); doc.text(String(v), 60, y); y += 7; });
  if (r.notes) { doc.setTextColor(120, 120, 120); doc.text("Notes", 14, y); doc.setTextColor(20, 20, 20); doc.text(String(r.notes).slice(0, 90), 60, y); y += 7; }
  y += 8; doc.setDrawColor(...gold); doc.setLineWidth(0.8); doc.line(14, y, 196, y); y += 11;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...blue);
  doc.text("Total Due", 14, y);
  doc.text(`AED ${Number(r.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, 196, y, { align: "right" });
  doc.save(`${r.dispatch_invoice_number}.pdf`);
};

const Dt = ({ k, children, testid }) => <div><dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt><dd className="mt-1" data-testid={testid}>{children}</dd></div>;

function ShipmentContext({ details, loading }) {
  if (loading) return <p className="text-sm text-muted-foreground" data-testid="dispatch-invoice-details-loading">Loading shipment details…</p>;
  if (!details?.has_dispatch) return <p className="text-sm text-muted-foreground" data-testid="dispatch-invoice-no-dispatch">No dispatch is linked to this invoice.</p>;
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Dt k="AWB / Tracking #" testid="dispatch-invoice-view-awb"><span className="font-medium">{details.awb || "—"}</span></Dt>
        <Dt k="Mode">{details.mode === "air_cargo" ? `Air cargo${details.airline_name ? ` · ${details.airline_name}` : ""}` : `Courier${details.courier_name ? ` · ${details.courier_name}` : ""}`}</Dt>
        {details.destination_country && <Dt k="Destination">{details.destination_country}</Dt>}
      </dl>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">PTN details</p>
        {details.patients?.length > 0 ? <ul className="space-y-1" data-testid="dispatch-invoice-view-ptn">{details.patients.map((p, i) => <li key={i} className="text-sm flex items-center gap-2"><span className="font-medium">{p.patient_name || "—"}</span>{p.order_number && <span className="text-xs text-muted-foreground">· {p.order_number}</span>}</li>)}</ul>
          : <p className="text-sm" data-testid="dispatch-invoice-view-ptn">{details.ptn_name || "—"}</p>}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Items shipped</p>
        {details.items?.length > 0 ? (
          <div className="border border-border rounded-md overflow-hidden" data-testid="dispatch-invoice-view-items"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border/60 bg-muted/40"><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Order #</th></tr></thead>
            <tbody>{details.items.map((it, i) => <tr key={i} className="border-b border-border/40 last:border-0"><td className="px-3 py-2 font-medium">{it.name || "—"}</td><td className="px-3 py-2 text-right">{it.quantity ?? "—"}</td><td className="px-3 py-2 text-muted-foreground">{it.order_number || "—"}</td></tr>)}</tbody>
          </table></div>
        ) : <p className="text-sm text-muted-foreground">No items recorded on this dispatch.</p>}
      </div>
    </div>
  );
}

/** Compact list of DINV invoices raised on one dispatch job, each opening the shared view dialog. */
export function DispatchInvoicesCard({ dispatchId }) {
  const [rows, setRows] = useState(null);
  const [viewing, setViewing] = useState(null);
  useEffect(() => { api.get("/dispatch-invoices").then((r) => setRows(r.data.filter((x) => x.dispatch_id === dispatchId))).catch(() => setRows([])); }, [dispatchId]);
  if (!rows?.length) return null;
  const total = rows.reduce((a, r) => a + (r.amount || 0), 0);
  return (
    <div className="hp-card overflow-hidden" data-testid="dispatch-invoices-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold">Dispatch invoices <span className="text-xs font-normal text-muted-foreground">(AED)</span></h3>
        <span className="text-sm font-semibold tabular-nums" data-testid="dispatch-invoices-card-total">{aed(total)}</span>
      </div>
      {rows.map((r) => (
        <button key={r.id} onClick={() => setViewing(r)} className="w-full flex items-center justify-between px-4 py-2.5 border-b border-border last:border-0 hover:bg-primary/5 text-left" data-testid={`dispatch-invoice-card-${r.dispatch_invoice_number}`}>
          <div><p className="font-medium text-sm text-primary">{r.dispatch_invoice_number}</p><p className="text-xs text-muted-foreground">{chargeLabel(r)} · {fmtDateTime(r.created_at)}</p></div>
          <div className="flex items-center gap-3"><span className="text-sm font-semibold tabular-nums">{aed(r.amount)}</span><StatusBadge status={r.status} /></div>
        </button>
      ))}
      <DispatchInvoiceViewDialog invoice={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
export function DispatchInvoiceViewDialog({ invoice, onClose, canEdit, onEdit, canPay, onPaid, canDelete, onDelete }) {
  const [viewing, setViewing] = useState(invoice);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setViewing(invoice); setDetails(null);
    if (!invoice?.id) return;
    setLoading(true);
    const need = !invoice.dispatch_invoice_number;
    Promise.all([api.get(`/dispatch-invoices/${invoice.id}/details`), need ? api.get("/dispatch-invoices") : null])
      .then(([d, list]) => { setDetails(d.data); if (list) setViewing(list.data.find((r) => r.id === invoice.id) || invoice); })
      .catch(() => setDetails(null)).finally(() => setLoading(false));
  }, [invoice]);
  const v = viewing;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-invoice-view-dialog">
        <DialogHeader><DialogTitle>Dispatch Invoice {v?.dispatch_invoice_number || ""}</DialogTitle></DialogHeader>
        {v && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md bg-muted/50 border border-border px-4 py-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Amount due</span>
              <span className="text-xl font-heading font-bold" data-testid="dispatch-invoice-view-amount">{v.amount != null ? aed(v.amount) : "…"}</span>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <Dt k="Status">{v.status ? <StatusBadge status={v.status} /> : "—"}</Dt>
              <Dt k="Type">{v.charge_type ? chargeLabel(v) : "—"}</Dt>
              <Dt k="Dispatch #">{v.dispatch_number || "—"}</Dt>
              <Dt k="Order #">{v.order_number || "—"}</Dt>
              <Dt k="Shipment #">{v.shipment_number || "—"}</Dt>
              <Dt k="Issued by">{v.issued_by_name || "—"}</Dt>
              <Dt k="Issued">{v.created_at ? fmtDateTime(v.created_at) : "—"}</Dt>
              {v.updated_at && <Dt k="Last edited">{fmtDateTime(v.updated_at)}{v.updated_by_name ? ` · ${v.updated_by_name}` : ""}</Dt>}
            </dl>
            {v.notes && <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes</p><p className="text-sm">{v.notes}</p></div>}
            <div className="border-t border-border pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3 font-semibold">Shipment details</p>
              <ShipmentContext details={details} loading={loading} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              {canDelete && v.status && v.status !== "paid" && <Button variant="ghost" size="sm" className="rounded-sm mr-auto text-destructive hover:text-destructive" onClick={() => onDelete(v)} data-testid="dispatch-invoice-view-delete">Delete</Button>}
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadDispatchInvoicePdf(v)} disabled={!v.dispatch_invoice_number} data-testid="dispatch-invoice-view-pdf">Download PDF</Button>
              {canEdit?.(v) && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => onEdit(v)} data-testid="dispatch-invoice-view-edit">Edit</Button>}
              {canPay && v.status && v.status !== "paid" && <Button size="sm" className="rounded-sm" onClick={() => onPaid(v)} data-testid="dispatch-invoice-view-paid">Mark paid</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
